import { useMemo } from 'react';
import { S, t, tList } from '../i18n/strings';
import { useActions, useSteve } from '../state/context';
import type { MixState, PresetName } from '../state/types';
import { MIX_RANGES, PAD_COUNT, PAD_TIMES } from '../state/types';

/** Un curseur du mixeur, prêt à être habillé par le skin. */
export interface SliderModel {
  readonly key: keyof typeof MIX_RANGES;
  readonly label: string;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly value: number;
  readonly valueLabel: string;
  readonly onChange: (value: number) => void;
}

export interface PadModel {
  readonly index: number;
  readonly label: string;
  readonly timeLabel: string;
  readonly onHit: () => void;
}

export interface PresetModel {
  readonly name: PresetName;
  readonly label: string;
  readonly onSelect: () => void;
}

export interface MixerModel {
  readonly pads: readonly PadModel[];
  readonly sliders: readonly SliderModel[];
  readonly presets: readonly PresetModel[];
  readonly holdLabel: string;
  readonly stopLabel: string;
  readonly wobbleLabel: string;
  readonly stateLabel: string;
  readonly onToggleHold: () => void;
  readonly onStop: () => void;
  readonly onToggleWobble: () => void;
}

/**
 * Toute la logique du mixeur, indépendante de l'habillage.
 *
 * Les trois skins affichent les mêmes valeurs avec des chromes radicalement
 * différents : ce hook produit les libellés et les gestes, chaque skin ne
 * s'occupe plus que de la mise en forme.
 */
export function useMixerModel(): MixerModel {
  const state = useSteve();
  const actions = useActions();
  const { lang, mix, hold, playing } = state;

  return useMemo<MixerModel>(() => {
    const setMix = (patch: Partial<MixState>): void => {
      actions.setMix(patch);
    };

    // « 125 % » en français, « 125% » en anglais.
    const percent = (value: number): string =>
      lang === 'fr' ? `${String(Math.round(value))} %` : `${String(Math.round(value))}%`;

    const padLabels = tList(S.mixer.pads, lang);

    const pads: PadModel[] = Array.from({ length: PAD_COUNT }, (_, index) => ({
      index,
      label: padLabels[index] ?? '?',
      // Les timecodes ne changent pas de langue (handoff § i18n).
      timeLabel: `${(PAD_TIMES[index] ?? 0).toFixed(1)}s`,
      onHit: () => {
        actions.hitPad(index);
      },
    }));

    const sliders: SliderModel[] = [
      {
        key: 'rate',
        label: t(S.mixer.speed, lang),
        ...MIX_RANGES.rate,
        value: mix.rate,
        valueLabel: `×${mix.rate.toFixed(2)}`,
        onChange: (value) => {
          setMix({ rate: value });
        },
      },
      {
        key: 'cutoff',
        label: t(S.mixer.depth, lang),
        ...MIX_RANGES.cutoff,
        value: mix.cutoff,
        // Au-delà de 17 kHz le filtre ne coupe plus rien d'audible : on le dit.
        valueLabel:
          mix.cutoff >= 17000 ? t(S.mixer.open, lang) : `${String(Math.round(mix.cutoff))} Hz`,
        onChange: (value) => {
          setMix({ cutoff: value });
        },
      },
      {
        key: 'echo',
        label: t(S.mixer.echo, lang),
        ...MIX_RANGES.echo,
        value: mix.echo,
        valueLabel: percent(mix.echo * 125),
        onChange: (value) => {
          setMix({ echo: value });
        },
      },
      {
        key: 'vol',
        label: t(S.mixer.level, lang),
        ...MIX_RANGES.vol,
        value: mix.vol,
        valueLabel: percent(mix.vol * 100),
        onChange: (value) => {
          setMix({ vol: value });
        },
      },
    ];

    const presetDefs = [
      { name: 'normal', label: t(S.mixer.preset_normal, lang) },
      { name: 'deep', label: t(S.mixer.preset_deep, lang) },
      { name: 'fast', label: t(S.mixer.preset_fast, lang) },
      { name: 'choir', label: t(S.mixer.preset_choir, lang) },
    ] as const;

    const presets: PresetModel[] = presetDefs.map(({ name, label }) => ({
      name,
      label,
      onSelect: () => {
        actions.preset(name);
      },
    }));

    return {
      pads,
      sliders,
      presets,
      holdLabel: t(hold ? S.mixer.hold_on : S.mixer.hold_off, lang),
      stopLabel: t(S.mixer.stop, lang),
      wobbleLabel: t(mix.wobble ? S.mixer.wobble_on : S.mixer.wobble_off, lang),
      stateLabel: t(playing ? S.mixer.state_signal : S.mixer.state_silence, lang),
      onToggleHold: actions.toggleHold,
      onStop: actions.stopPad,
      onToggleWobble: actions.toggleWobble,
    };
  }, [actions, lang, mix, hold, playing]);
}
